import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Wifi, Globe } from "lucide-react";

/**
 * PartnerOffers — A1 5G + MTel Welt-Roaming Banner-Sektion.
 * Die ehemals integrierte "Tarif zu teuer?" + "Refurbished"-Reihe wurde
 * extrahiert nach TariffAndRefurbished.jsx, damit sie weiter oben auf der
 * Landing-Page (zwischen GeräteretterPrämie und KI-Ankauf) platziert ist.
 */
export default function PartnerOffers() {
  return (
    <section className="relative py-24 md:py-28 bg-[#0F0F12] overflow-hidden" data-testid="partner-offers-section">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">

        <div className="grid md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Aktionen &amp; Services</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-white mt-3 leading-[1.05]">
              Mehr als nur<br/>
              <span className="text-zinc-500">Reparatur.</span>
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-4">
            <p className="text-lg text-zinc-400 leading-relaxed">
              A1 5G im stärksten Netz Österreichs. MTel für günstiges Roaming in 40+ Ländern.
              Tarif-Beratung und Vertragsabschluss direkt bei uns im Shop.
            </p>
          </div>
        </div>

        {/* Row 1: A1 5G — full width */}
        <div className="grid md:grid-cols-12 gap-5 md:gap-6">

          {/* A1 5G Banner — full width hero card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-12 relative overflow-hidden rounded-[28px] border border-[#DC1928]/30 group"
            data-testid="a1-5g-card"
          >
            <div className="relative aspect-[16/9] md:aspect-[21/8]">
              <img
                src="/images/store/a1-5g-banner.jpeg"
                alt="A1 5G — Ganz Österreich"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-transparent to-transparent"></div>

              <div className="absolute top-5 left-5 md:top-7 md:left-9 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/65 backdrop-blur-md border border-white/15">
                <Wifi className="w-3.5 h-3.5 text-[#FF2D40]" />
                <span className="text-[10px] uppercase tracking-[0.22em] text-white font-bold">A1 5G Tarif</span>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-7 md:p-10">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold drop-shadow">A1 Exclusive Partner</div>
                <h3 className="font-display text-3xl md:text-5xl font-bold text-white mt-2 leading-[1.02]">
                  5G&nbsp;ganz<br className="hidden sm:block"/> Österreich.
                </h3>
                <p className="text-sm md:text-base text-zinc-200 mt-3 max-w-md leading-relaxed">
                  A1 baut das 5Giganetz — die beste Kombination aus größtem Glasfasernetz und schnellster 5G-Technologie.
                  Tarif-Beratung direkt bei uns.
                </p>
                <Link
                  to="/kontakt"
                  data-testid="a1-5g-cta"
                  className="inline-flex items-center gap-1.5 mt-5 px-5 h-11 rounded-full bg-white text-[#0A0A0B] font-medium btn-lift hover:bg-zinc-100 transition-colors"
                >
                  Beratung anfragen <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Row 2: MTEL WELT wide hero banner — banner has its own baked-in text, we only add badge + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-5 md:mt-6 relative overflow-hidden rounded-[28px] border border-[#3D5BFF]/30 group"
          data-testid="mtel-welt-card"
        >
          <div className="relative aspect-[21/9] md:aspect-[24/7]">
            <img
              src="/images/store/mtel-welt-banner.png"
              alt="MTel Welt — Roame in 40+ Ländern, ohne Zusatzkosten"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
              loading="lazy"
            />
            {/* Top-left badge */}
            <div className="absolute top-5 left-5 md:top-7 md:left-9 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/65 backdrop-blur-md border border-white/15">
              <Globe className="w-3.5 h-3.5 text-[#FF7B8E]" />
              <span className="text-[10px] uppercase tracking-[0.22em] text-white font-bold">MTel Vertriebspartner</span>
            </div>

            {/* CTA — bottom-left */}
            <div className="absolute bottom-5 left-5 md:bottom-8 md:left-9">
              <Link
                to="/kontakt"
                data-testid="mtel-welt-cta"
                className="inline-flex items-center gap-1.5 px-5 h-11 rounded-full bg-white text-[#0A0A0B] font-medium btn-lift hover:bg-zinc-100 transition-colors shadow-xl"
              >
                MTel beraten lassen <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}
